import { BrandVariants, createLightTheme, Theme } from '@fluentui/react-components';

import { STROKE, ACCENT, FONT, RADIUS, STATE, SURFACE, TEXT } from './tokens';

// The AirBridge Fluent theme, applied ONCE at the ROOT provider in `main.tsx`.
// It superseded `rubixTheme` in FD-466.
//
// This comment block described a PREVIEW overlay until FD-469: the theme was
// first built for a nested provider at the `/preview` route root, beside an
// untouched orange production shell. Both of those are gone — there is one
// shell, one provider and one theme — and the note is rewritten rather than
// deleted, because the FD-060 rule it carried still binds and is the reason
// nobody should reintroduce a second provider.
//
// DO NOT ADD A NESTED, DYNAMICALLY-SCOPED PROVIDER (FD-060). That bug's
// hover-flicker came from a provider re-created on render inside an
// interaction-sensitive subtree, so Griffel re-injected its stylesheet on every
// hover. Root-level theming is safe precisely because it mounts once and holds
// its theme for the process lifetime; the constant below is module-level for the
// same reason — never an object literal built in render, never keyed.
//
// If a control looks wrong, the TOKEN is wrong: fix it here, where every control
// sees the same value, rather than overriding a brand token per component.

// The ramp is centred on AirBridge Blue at brand80 — the rung Fluent resolves
// `colorBrandBackground`, `colorCompoundBrand*` and the link tokens from.
//
// brand80 is `#1677F2` and its white foreground is 4.24:1, BELOW AA. That is
// FD-ADR-043 D5 — an accepted deviation, recorded in ./contrast.md, the ADR and
// the design record. Do not darken this rung to make a linter happy; the
// decision was taken with the number in view.
const airbridgeBrand: BrandVariants = {
  10: '#020B18',
  20: '#04142B',
  30: '#061F42',
  40: '#08295A',
  50: '#0A3472',
  60: '#0B4090',
  70: '#0F59BC',
  80: '#1677F2',
  90: '#3689F4',
  100: '#559BF6',
  110: '#74ADF8',
  120: '#92BFFA',
  130: '#AFD0FB',
  140: '#C9DFFC',
  150: '#DDEBFE',
  160: '#EFF6FF',
};

const base = createLightTheme(airbridgeBrand);

export const airbridgeTheme: Theme = {
  ...base,

  // Type. Inter replaces Geist everywhere, INCLUDING inside Fluent controls —
  // the mockups render controls in the brand face, so leaving Segoe UI Variable
  // in place would give buttons and inputs a different voice from the page they
  // sit on. `fontFamilyMonospace` is Inter too: FD-ADR-043 D2 retires the mono
  // family and numerics use `tabular-nums` instead.
  fontFamilyBase: FONT.display,
  fontFamilyNumeric: FONT.display,
  fontFamilyMonospace: FONT.display,

  // Text on a brand surface. WHITE, per D5 — see the ramp comment above.
  colorNeutralForegroundOnBrand: ACCENT.onBase,

  // The soft tint family, so a `Badge appearance="tint"` reads as the design
  // board's in-progress chip rather than Fluent's default brand fill.
  colorBrandBackground2: ACCENT.tint,
  colorBrandBackground2Hover: '#D6E7FF',
  colorBrandBackground2Pressed: '#C4DBFC',

  colorNeutralBackground1: SURFACE.base,
  colorNeutralForeground1: TEXT.primary,
  colorNeutralForeground2: TEXT.secondary,

  // Mist on DIVIDERS only. `colorNeutralStroke1` — which Fluent uses for input
  // and control borders — is deliberately left at its default: Mist is 1.26:1 on
  // white, so a control whose only boundary was Mist would fail 1.4.11 and be
  // genuinely hard to see.
  colorNeutralStroke2: STROKE.base,
  colorNeutralStroke3: STROKE.subtle,

  // Status hues aligned to the AirBridge board so a Fluent `MessageBar`,
  // `Badge color="danger"` or validation state agrees with our own `StatePill`
  // rather than sitting a shade away from it.
  colorPaletteGreenForeground1: STATE.success.text,
  colorPaletteGreenBackground2: STATE.success.soft,
  colorPaletteRedForeground1: STATE.critical.text,
  colorPaletteRedBackground2: STATE.critical.soft,
  colorPaletteYellowForeground1: STATE.warning.text,
  colorPaletteYellowBackground2: STATE.warning.soft,

  // Geometry. FD-470, owner decision 2026-09-25 — the board's controls are
  // rounder than Fluent's 2/4/6/8 ramp, and the decision was taken with the
  // blast radius explicitly in view: this is EVERY Fluent control in the
  // console, not the page CTA it was asked for. Buttons, inputs, dropdowns,
  // tabs, menus, badges, message bars, dialogs and roughly twenty DataTable
  // consumers all move in one commit.
  //
  // `borderRadiusMedium` is the one that matters — Fluent's Button, Input,
  // Dropdown and Card all read it — so the other three are scaled around it to
  // keep the ramp monotonic rather than pinned to the board's one measurement.
  //
  // Height is NOT changed here. The board's CTA is taller as well as rounder,
  // and that is `size="large"` on the instance (40px, 16px type) rather than a
  // global control height: raising every control's height would re-flow every
  // dense table toolbar in the console for the sake of one button.
  borderRadiusSmall: RADIUS.sm,
  borderRadiusMedium: RADIUS.md,
  borderRadiusLarge: RADIUS.lg,
  borderRadiusXLarge: RADIUS.xl,
};
